import { type MouseEvent as ReactMouseEvent } from 'react'
import { type History } from 'history'
import { type useTrackEvent } from '@retailer-platform/dashboard/analytics'

type TrackEvent = ReturnType<typeof useTrackEvent>

// Fires an optional tracking callback then routes to an internal SPA path.
// Falls through to default anchor behavior for modifier-clicks so the user
// can still open the link in a new tab. Prevents same-tab full-page unload
// on plain click, which would otherwise cancel the in-flight tracking request.
export const handleTrackedNavigation = (
  event: ReactMouseEvent<HTMLAnchorElement>,
  { history, url, track }: { history: History; url: string; track?: () => void }
): void => {
  track?.()
  if (event.defaultPrevented) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  if (event.button !== 0) return
  event.preventDefault()
  history.push(url)
}

const LANDING_EVENT_ID = 'care_landing_page.interaction'
const CUSTOMER_DETAILS_EVENT_ID = 'care_customer_details.interaction'
const CUSTOMER_ORDER_HISTORY_EVENT_ID = 'care_order_history.interaction'
const ORDER_DETAIL_EVENT_ID = 'care_order_detail.interaction'

export type DestinationPage = 'customer_details' | 'customer_orders' | 'order_details'

// Identifies the page a shared component is rendered on, so tracking helpers
// only fire from the intended host page. Add a variant when a new host page
// needs tracking.
export enum TrackingSourcePage {
  CustomerDetail = 'customer_detail',
  CustomerOrderHistory = 'customer_order_history',
}

export enum AdminLinksDropdownPage {
  CustomerDetail = 'customer_detail',
  OrderDetail = 'order_detail',
}

// Section within the personal information block a link was clicked from.
// Values are the exact strings the RISE metrics spec expects.
export enum PersonalInfoSection {
  Main = 'Main',
  FullDetail = 'Full detail',
}

export enum ShopperBatchRole {
  Picker = 'picker',
  Runner = 'runner',
  Delivering = 'delivering',
}

export enum AllOrdersType {
  Grocery = 'grocery',
  Restaurant = 'restaurant',
}

export enum ViewPhotosSource {
  Window = 'window',
  Click = 'click',
}

export enum DrawerActivityTab {
  All = 'all',
  Chats = 'chats',
  Notes = 'notes',
  Activity = 'activity',
}

export enum AdminLinkElement {
  LastGroceryOrder = 'last_grocery_order',
  FraudMarkers = 'fraud_markers',
  AbTests = 'ab_tests',
  CartItems = 'cart_items',
  Cases = 'cases',
  ChargesRefunds = 'charges_refunds',
  Donations = 'donations',
  FraudScore = 'fraud_score',
  FreshFunds = 'fresh_funds',
  HoldsAndFlags = 'holds_and_flags',
  Notifications = 'notifications',
  PromosCredits = 'promos_credits',
  PromotionSearch = 'promotion_search',
  Subscriptions = 'subscriptions',
  Timeline = 'timeline',
  Visits = 'visits',
  MarqetaTransactions = 'marqeta_transactions',
}

export enum ManageCustomerAction {
  LoginAsCustomer = 'login_as_customer',
  EditCustomer = 'edit_customer',
  MarkCcFraud = 'mark_cc_fraud',
  MarkCouponAbuse = 'mark_coupon_abuse',
  MarkFdoFraud = 'mark_fdo_fraud',
  DeactivateAccount = 'deactivate_account',
}

export enum PersonalInfoField {
  ContactNumber = 'contact_number',
  Address = 'address',
}

export const trackHeaderAdminLinksOpened = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Admin links dropdown on the care customer details page',
    data: {
      interactionKey: 'header.click.admin_links',
      customerId,
    },
  })
}

export const trackHeaderManageCustomerOpened = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Manage customer dropdown on the care customer details page',
    data: {
      interactionKey: 'header.click.manage_customer',
      customerId,
    },
  })
}

export const trackHeaderAdminLinkNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    element,
    orderId,
  }: { customerId: string; element: AdminLinkElement; orderId?: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: `User navigated to ${element} from the Admin links dropdown on the care customer details page`,
    data: {
      interactionKey: `header.navigate.${element}`,
      destinationPage: element,
      customerId,
      ...(orderId ? { orderId } : {}),
    },
  })
}

export const trackHeaderManageCustomerAction = (
  trackEvent: TrackEvent,
  { customerId, action }: { customerId: string; action: ManageCustomerAction }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: `User clicked ${action} from the Manage customer dropdown on the care customer details page`,
    data: {
      interactionKey: `header.click.${action}`,
      customerId,
    },
  })
}

export const trackHeaderEditCustomerClosed = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User closed the Edit customer modal on the care customer details page',
    data: {
      interactionKey: 'header.close.edit_customer',
      customerId,
    },
  })
}

export const trackHeaderPersonalInfoUpdated = (
  trackEvent: TrackEvent,
  {
    customerId,
    field,
    oldValue,
    newValue,
  }: { customerId: string; field: PersonalInfoField; oldValue: string; newValue: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: `User updated ${field} from the Edit customer modal on the care customer details page`,
    data: {
      interactionKey: `header.update.${field}`,
      customerId,
      context: { oldValue, newValue },
    },
  })
}

export const trackHeaderBusinessAccountNavigated = (
  trackEvent: TrackEvent,
  { customerId, targetCustomerId }: { customerId: string; targetCustomerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User navigated to a business account from the Edit customer modal on the care customer details page',
    data: {
      interactionKey: 'header.navigate.business_account',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { targetCustomerId },
    },
  })
}

export const trackHeaderLinkedAccountNavigated = (
  trackEvent: TrackEvent,
  { customerId, targetCustomerId }: { customerId: string; targetCustomerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User navigated to a linked account from the Edit customer modal on the care customer details page',
    data: {
      interactionKey: 'header.navigate.linked_account',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { targetCustomerId },
    },
  })
}

export const trackHeaderLinkedAccountDeleted = (
  trackEvent: TrackEvent,
  { customerId, targetCustomerId }: { customerId: string; targetCustomerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User removed a linked account from the Edit customer modal on the care customer details page',
    data: {
      interactionKey: 'header.delete.linked_account',
      customerId,
      context: { targetCustomerId },
    },
  })
}

export const trackRecentlyVisitedCustomerNavigated = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description:
      'User navigated to a customer profile from the recently visited list on the care customer search landing page',
    data: {
      interactionKey: 'recently_visited.navigate.customer_profile',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
    },
  })
}

export const trackRecentlyVisitedShopperNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, shopperUrl }: { customerId: string; orderId: string; shopperUrl: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description:
      'User clicked a shopper on a recently visited customer card on the care customer search landing page',
    data: {
      interactionKey: 'recently_visited.navigate.shopper_profile',
      destinationPage: shopperUrl,
      customerId,
      orderId,
    },
  })
}

export const trackFastFindCustomerNavigated = (
  trackEvent: TrackEvent,
  { customerId, sourcePage }: { customerId: string; sourcePage: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description: 'User navigated to a customer profile from the Fast Find modal',
    data: {
      interactionKey: 'fast_find.navigate.customer_profile',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { sourcePage },
    },
  })
}

export const trackFastFindOrderNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, sourcePage }: { customerId: string; orderId: string; sourcePage: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description: 'User navigated to order details from the Fast Find modal',
    data: {
      interactionKey: 'fast_find.navigate.order_details',
      destinationPage: 'order_details' satisfies DestinationPage,
      customerId,
      orderId,
      context: { sourcePage },
    },
  })
}

export const trackSearchBarCustomerNavigated = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description:
      'User navigated to a customer profile from the search bar on the care customer search landing page',
    data: {
      interactionKey: 'search_bar.navigate.customer_profile',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
    },
  })
}

export const trackSearchBarOrderNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId }: { customerId: string; orderId: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description:
      'User navigated to order details from the search bar on the care customer search landing page',
    data: {
      interactionKey: 'search_bar.navigate.order_details',
      destinationPage: 'order_details' satisfies DestinationPage,
      customerId,
      orderId,
    },
  })
}

export const trackFastFindModalOpened = (
  trackEvent: TrackEvent,
  { sourcePage }: { sourcePage: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description: 'User opened the Fast Find modal',
    data: {
      interactionKey: 'fast_find.click.modal_open',
      context: { sourcePage },
    },
  })
}

export const trackFastFindModalClosed = (
  trackEvent: TrackEvent,
  { sourcePage }: { sourcePage: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description: 'User closed the Fast Find modal',
    data: {
      interactionKey: 'fast_find.click.modal_close',
      context: { sourcePage },
    },
  })
}

export const trackFastFindSearchClicked = (
  trackEvent: TrackEvent,
  { sourcePage }: { sourcePage: string }
) => {
  trackEvent({
    id: LANDING_EVENT_ID,
    description: 'User clicked Search in the Fast Find modal',
    data: {
      interactionKey: 'fast_find.click.search',
      context: { sourcePage },
    },
  })
}

// ---------- Customer Order History page ----------

export const trackAllOrdersOrderDetailsNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    orderType,
  }: { customerId: string; orderId: string; orderType: AllOrdersType }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description:
      'User navigated to order details from the all orders grid on the customer order history page',
    data: {
      interactionKey: 'all_orders.navigate.order_details',
      destinationPage: 'order_details' satisfies DestinationPage,
      customerId,
      orderId,
      context: { orderType },
    },
  })
}

export const trackAllOrdersShopperProfileNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, shopperUrl }: { customerId: string; orderId: string; shopperUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description:
      'User navigated to a shopper profile from the all orders grid on the customer order history page',
    data: {
      interactionKey: 'all_orders.navigate.shopper_profile',
      destinationPage: shopperUrl,
      customerId,
      orderId,
    },
  })
}

export const trackAllOrdersGroceryTabClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User selected the grocery orders tab on the customer order history page',
    data: {
      interactionKey: 'all_orders.tab.grocery_orders',
      customerId,
    },
  })
}

export const trackAllOrdersRestaurantTabClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User selected the restaurant orders tab on the customer order history page',
    data: {
      interactionKey: 'all_orders.tab.restaurant_orders',
      customerId,
    },
  })
}

export const trackAllOrdersDateFilterChanged = (
  trackEvent: TrackEvent,
  { customerId, dateRange }: { customerId: string; dateRange: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User changed the date range filter on the all orders grid',
    data: {
      interactionKey: 'all_orders.filter.date_filter',
      customerId,
      context: { dateRange },
    },
  })
}

export const trackAllOrdersRetailerFilterChanged = (
  trackEvent: TrackEvent,
  { customerId, retailer }: { customerId: string; retailer: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User changed the retailer filter on the all orders grid',
    data: {
      interactionKey: 'all_orders.filter.retailer',
      customerId,
      context: { retailer },
    },
  })
}

export const trackAllOrdersDrawerOpened = (
  trackEvent: TrackEvent,
  { customerId, orderId }: { customerId: string; orderId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User opened the order history drawer from the all orders grid',
    data: {
      interactionKey: 'all_orders.click.drawer',
      customerId,
      orderId,
    },
  })
}

export const trackRecentOrderDriverRouteClicked = (
  trackEvent: TrackEvent,
  { customerId, orderId }: { customerId: string; orderId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User clicked the driver route link on the most recent order card',
    data: {
      interactionKey: 'recent_order.click.driver_route',
      customerId,
      orderId,
    },
  })
}

export const trackRecentOrderPhotosNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    photosUrl,
    source,
  }: { customerId: string; orderId: string; photosUrl: string; source: ViewPhotosSource }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User opened delivery photos from the most recent order card',
    data: {
      interactionKey: 'recent_order.navigate.view_photos',
      destinationPage: photosUrl,
      customerId,
      orderId,
      context: { source },
    },
  })
}

export const trackRecentOrderReceiptNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, receiptUrl }: { customerId: string; orderId: string; receiptUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User opened the order receipt from the most recent order card',
    data: {
      interactionKey: 'recent_order.navigate.view_receipt',
      destinationPage: receiptUrl,
      customerId,
      orderId,
    },
  })
}

export const trackRecentOrderLogisticsNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    logisticsUrl,
  }: { customerId: string; orderId: string; logisticsUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to logistics from the most recent order card',
    data: {
      interactionKey: 'recent_order.navigate.logistics',
      destinationPage: logisticsUrl,
      customerId,
      orderId,
    },
  })
}

export const trackRecentOrderDropOffLocationClicked = (
  trackEvent: TrackEvent,
  { customerId, orderId }: { customerId: string; orderId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User clicked the drop off location link on the most recent order card',
    data: {
      interactionKey: 'recent_order.click.drop_off_location',
      customerId,
      orderId,
    },
  })
}

export const trackRecentOrderBatchNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    batchRole,
    batchUrl,
  }: { customerId: string; orderId: string; batchRole: ShopperBatchRole; batchUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to a shopper batch from the most recent order card',
    data: {
      interactionKey: 'recent_order.navigate.batch',
      destinationPage: batchUrl,
      customerId,
      orderId,
      context: { batchRole },
    },
  })
}

export const trackDrawerOrderDetailsNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId }: { customerId: string; orderId: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to full order details from the order history drawer',
    data: {
      interactionKey: 'drawer.navigate.order_details',
      destinationPage: 'order_details' satisfies DestinationPage,
      customerId,
      orderId,
    },
  })
}

export const trackDrawerShopperProfileNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, shopperUrl }: { customerId: string; orderId: string; shopperUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to a shopper profile from the order history drawer',
    data: {
      interactionKey: 'drawer.navigate.shopper_profile',
      destinationPage: shopperUrl,
      customerId,
      orderId,
    },
  })
}

export const trackDrawerBatchNavigated = (
  trackEvent: TrackEvent,
  { customerId, orderId, batchUrl }: { customerId: string; orderId: string; batchUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to a shopper batch from the order history drawer',
    data: {
      interactionKey: 'drawer.navigate.batch',
      destinationPage: batchUrl,
      customerId,
      orderId,
    },
  })
}

const DRAWER_ACTIVITY_TAB_INTERACTION_KEY: Record<DrawerActivityTab, string> = {
  [DrawerActivityTab.All]: 'drawer.tab.activity_feed_all',
  [DrawerActivityTab.Chats]: 'drawer.tab.activity_feed_chats',
  [DrawerActivityTab.Notes]: 'drawer.tab.activity_feed_notes',
  [DrawerActivityTab.Activity]: 'drawer.tab.activity_feed_activity',
}

export const trackDrawerActivityFeedTabClicked = (
  trackEvent: TrackEvent,
  { customerId, orderId, tab }: { customerId: string; orderId: string; tab: DrawerActivityTab }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User switched the activity feed tab inside the order history drawer',
    data: {
      interactionKey: DRAWER_ACTIVITY_TAB_INTERACTION_KEY[tab],
      customerId,
      orderId,
    },
  })
}

export const trackDrawerFullHistoryNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    fullHistoryUrl,
  }: { customerId: string; orderId: string; fullHistoryUrl: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User navigated to the full customer notes history from the order history drawer',
    data: {
      interactionKey: 'drawer.navigate.full_history',
      destinationPage: fullHistoryUrl,
      customerId,
      orderId,
    },
  })
}

// ---------- Customer Details page: Recent orders card ----------

export const trackRecentOrdersGroceryTabClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User selected the grocery orders tab on the customer details recent orders card',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.tab.grocery_orders',
      customerId,
    },
  })
}

export const trackRecentOrdersRestaurantTabClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User selected the restaurant orders tab on the customer details recent orders card',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.tab.restaurant_orders',
      customerId,
    },
  })
}

export const trackRecentOrdersAllGroceryOrdersNavigated = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User clicked the See all orders button on the customer details recent orders card while the grocery tab was active',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.navigate.all_grocery_orders',
      destinationPage: 'customer_orders' satisfies DestinationPage,
      customerId,
    },
  })
}

export const trackRecentOrdersAllRestaurantOrdersNavigated = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description:
      'User clicked the See all orders button on the customer details recent orders card while the restaurant tab was active',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.navigate.all_restaurant_orders',
      destinationPage: 'customer_orders' satisfies DestinationPage,
      customerId,
    },
  })
}

export const trackRecentOrdersOrderIdNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    orderType,
    tileRank,
  }: { customerId: string; orderId: string; orderType: AllOrdersType; tileRank: number }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to order details from the customer details recent orders card',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.navigate.order_id',
      destinationPage: 'order_details' satisfies DestinationPage,
      customerId,
      orderId,
      context: { orderType, tileRank },
    },
  })
}

export const trackRecentOrdersBatchNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    batchUrl,
    orderType,
    tileRank,
  }: {
    customerId: string
    orderId: string
    batchUrl: string
    orderType: AllOrdersType
    tileRank: number
  }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a shopper batch from the customer details recent orders card',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.navigate.batch_id',
      destinationPage: batchUrl,
      customerId,
      orderId,
      context: { orderType, tileRank },
    },
  })
}

export const trackRecentOrdersShopperNameNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    orderId,
    shopperUrl,
    orderType,
    tileRank,
  }: {
    customerId: string
    orderId: string
    shopperUrl: string
    orderType: AllOrdersType
    tileRank: number
  }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a shopper profile from the customer details recent orders card',
    data: {
      pageName: 'customer_details',
      interactionKey: 'recent_orders.navigate.shopper_name',
      destinationPage: shopperUrl,
      customerId,
      orderId,
      context: { orderType, tileRank },
    },
  })
}

// ---------- Customer Details page: Personal information block ----------

export const trackPersonalInfoRelatedAccountNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    relatedCustomerId,
    section,
  }: {
    customerId: string
    relatedCustomerId: string
    section: PersonalInfoSection
  }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a related account from the personal information block',
    data: {
      pageName: 'customer_details',
      interactionKey: 'personal_info.navigate.related_account',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { customerId: relatedCustomerId, section },
    },
  })
}

export const trackPersonalInfoBusinessAccountNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    businessCustomerId,
    section,
  }: {
    customerId: string
    businessCustomerId: string
    section: PersonalInfoSection
  }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a business account from the personal information block',
    data: {
      pageName: 'customer_details',
      interactionKey: 'personal_info.navigate.business_account',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { customerId: businessCustomerId, section },
    },
  })
}

export const trackPersonalInfoLinkedAccountNavigated = (
  trackEvent: TrackEvent,
  {
    customerId,
    linkedCustomerId,
    section,
  }: {
    customerId: string
    linkedCustomerId: string
    section: PersonalInfoSection
  }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a linked account from the personal information block',
    data: {
      pageName: 'customer_details',
      interactionKey: 'personal_info.navigate.linked_account',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { customerId: linkedCustomerId, section },
    },
  })
}

export const trackPersonalInfoFullDetailsClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the full personal information drawer from the customer details page',
    data: {
      pageName: 'customer_details',
      interactionKey: 'personal_info.click.full_details',
      customerId,
    },
  })
}

export const trackPersonalInfoFullDetailsClosed = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User closed the full personal information drawer on the customer details page',
    data: {
      pageName: 'customer_details',
      interactionKey: 'personal_info.close.full_details',
      customerId,
    },
  })
}

export const trackDrawerAddNoteEntered = (
  trackEvent: TrackEvent,
  { customerId, orderId, note }: { customerId: string; orderId: string; note: string }
) => {
  trackEvent({
    id: CUSTOMER_ORDER_HISTORY_EVENT_ID,
    description: 'User submitted a note from the order history drawer',
    data: {
      interactionKey: 'drawer.enter.add_note',
      customerId,
      orderId,
      context: { note },
    },
  })
}

// ---------- Customer Details page: Instacart+ (IC+) membership block ----------

export const trackIcPlusFullDetailsClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Instacart+ membership drawer from the customer details page',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.click.full_details',
      customerId,
    },
  })
}

export const trackIcPlusFullDetailsClosed = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User closed the Instacart+ membership drawer on the customer details page',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.close.full_details',
      customerId,
    },
  })
}

export const trackIcPlusMembershipSwitched = (
  trackEvent: TrackEvent,
  { customerId, membershipRank }: { customerId: string; membershipRank: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User switched between memberships in the Instacart+ drawer',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.switch.membership',
      customerId,
      context: { membershipRank },
    },
  })
}

export const trackIcPlusFamilyMemberNavigated = (
  trackEvent: TrackEvent,
  { customerId, familyMemberCustomerId }: { customerId: string; familyMemberCustomerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User navigated to a family member from the Instacart+ household section',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.navigate.family_member',
      destinationPage: 'customer_details' satisfies DestinationPage,
      customerId,
      context: { customerId: familyMemberCustomerId },
    },
  })
}

export const trackIcPlusManageMembershipClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Instacart+ manage membership dropdown',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.click.manage_membership',
      customerId,
    },
  })
}

export const trackIcPlusAutoRenewNavigated = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Instacart+ toggle auto-renew modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.navigate.auto_renew',
      destinationPage: 'toggle_auto_renew',
      customerId,
    },
  })
}

export const trackIcPlusIssueRefundClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Instacart+ issue refund or credit modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.click.issue_refund',
      customerId,
    },
  })
}

export const trackIcPlusIssueRefundClosed = (
  trackEvent: TrackEvent,
  { customerId, refundSubmitted }: { customerId: string; refundSubmitted: boolean }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User closed the Instacart+ issue refund or credit modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.close.issue_refund',
      customerId,
      context: { refundSubmitted: refundSubmitted ? 'Y' : 'N' },
    },
  })
}

export const trackIcPlusRefundNumberEntered = (
  trackEvent: TrackEvent,
  { customerId, caseNumber }: { customerId: string; caseNumber: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User entered a Salesforce case number in the Instacart+ issue refund modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.enter.refund_number',
      customerId,
      context: { caseNumber },
    },
  })
}

export const trackIcPlusRefundConfirmationChecked = (
  trackEvent: TrackEvent,
  { customerId, confirmed }: { customerId: string; confirmed: boolean }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User toggled the confirmation checkbox in the Instacart+ issue refund modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.check.refund_confirmation',
      customerId,
      context: { confirmed: confirmed ? 'Y' : 'N' },
    },
  })
}

export const trackIcPlusAddFreeMonthClicked = (
  trackEvent: TrackEvent,
  { customerId }: { customerId: string }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User opened the Instacart+ add free month modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.click.add_free_month',
      customerId,
    },
  })
}

export const trackIcPlusAddFreeMonthConfirmationChecked = (
  trackEvent: TrackEvent,
  { customerId, confirmed }: { customerId: string; confirmed: boolean }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User toggled the confirmation checkbox in the Instacart+ add free month modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.check.add_free_month_confirmation',
      customerId,
      context: { confirmed: confirmed ? 'Y' : 'N' },
    },
  })
}

export const trackIcPlusAddFreeMonthClosed = (
  trackEvent: TrackEvent,
  { customerId, freeMonthSubmitted }: { customerId: string; freeMonthSubmitted: boolean }
) => {
  trackEvent({
    id: CUSTOMER_DETAILS_EVENT_ID,
    description: 'User closed the Instacart+ add free month modal',
    data: {
      pageName: 'customer_details',
      interactionKey: 'ic_plus.close.add_free_month',
      customerId,
      context: { freeMonthSubmitted: freeMonthSubmitted ? 'Y' : 'N' },
    },
  })
}

// ---------- Order Detail page: Manage order dropdown ----------

export const trackManageOrderResolveDeliveryIssueClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description:
      'User opened the Resolve delivery issue modal from the Manage order dropdown on the order details page',
    data: {
      interactionKey: 'manage_order.click.resolve_delivery_issue',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderIssueCreditClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description:
      'User opened the Issue credit modal from the Manage order dropdown on the order details page',
    data: {
      interactionKey: 'manage_order.click.issue_credit',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderModifyOrderClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description:
      'User opened the Modify order modal from the Manage order dropdown on the order details page',
    data: {
      interactionKey: 'manage_order.click.modify_order',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderForceCompleteClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description:
      'User opened the Force complete modal from the Manage order dropdown on the order details page',
    data: {
      interactionKey: 'manage_order.click.force_complete',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderDriversNavigated = (
  trackEvent: TrackEvent,
  { orderId, customerId, driversUrl }: { orderId: string; customerId: string; driversUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description:
      'User navigated to order drivers from the Manage order dropdown on the order details page',
    data: {
      interactionKey: 'manage_order.navigate.order_drivers',
      destinationPage: driversUrl,
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderResolveDeliveryIssueClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Resolve delivery issue modal on the order details page',
    data: {
      interactionKey: 'manage_order.close.resolve_delivery_issue',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderIssueCreditClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Issue credit modal on the order details page',
    data: {
      interactionKey: 'manage_order.close.issue_credit',
      orderId,
      customerId,
    },
  })
}

export const trackManageOrderForceCompleteClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Force complete modal on the order details page',
    data: {
      interactionKey: 'manage_order.close.force_complete',
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Resolve delivery issue modal ----------

export const trackResolveDeliveryIssueCaseNumberEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, caseNumber }: { orderId: string; customerId: string; caseNumber: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered a Salesforce case number in the Resolve delivery issue modal',
    data: {
      interactionKey: 'resolve_delivery_issue.enter.case_number',
      orderId,
      customerId,
      context: { caseNumber },
    },
  })
}

export const trackResolveDeliveryIssueTypeClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId, issueType }: { orderId: string; customerId: string; issueType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected an issue type in the Resolve delivery issue modal',
    data: {
      interactionKey: 'resolve_delivery_issue.click.issue_type',
      orderId,
      customerId,
      context: { issueType },
    },
  })
}

export const trackResolveDeliveryIssueResolutionTypeClicked = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    resolutionType,
  }: { orderId: string; customerId: string; resolutionType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a resolution type in the Resolve delivery issue modal',
    data: {
      interactionKey: 'resolve_delivery_issue.click.resolution_type',
      orderId,
      customerId,
      context: { resolutionType },
    },
  })
}

export const trackResolveDeliveryIssueResolved = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User submitted a resolution from the Resolve delivery issue modal',
    data: {
      interactionKey: 'resolve_delivery_issue.click.resolve_issue',
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Issue credit modal ----------

export const trackIssueCreditCaseNumberEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, caseNumber }: { orderId: string; customerId: string; caseNumber: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered a Salesforce case number in the Issue credit modal',
    data: {
      interactionKey: 'issue_credit.enter.case_number',
      orderId,
      customerId,
      context: { caseNumber },
    },
  })
}

export const trackIssueCreditSubmitted = (
  trackEvent: TrackEvent,
  { orderId, customerId, option }: { orderId: string; customerId: string; option: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User submitted a credit from the Issue credit modal',
    data: {
      interactionKey: 'issue_credit.click.submit',
      orderId,
      customerId,
      context: { option },
    },
  })
}

// ---------- Order Detail page: Force complete modal ----------

export const trackForceCompleteReasonClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId, reason }: { orderId: string; customerId: string; reason: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a reason in the Force complete modal',
    data: {
      interactionKey: 'force_complete.click.reason',
      orderId,
      customerId,
      context: { reason },
    },
  })
}

export const trackForceCompleteDescriptionEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, description }: { orderId: string; customerId: string; description: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered a description in the Force complete modal',
    data: {
      interactionKey: 'force_complete.enter.description',
      orderId,
      customerId,
      context: { description },
    },
  })
}

export const trackForceCompleteSubmitted = (
  trackEvent: TrackEvent,
  { orderId, customerId, reason }: { orderId: string; customerId: string; reason: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User submitted a force complete from the Force complete modal',
    data: {
      interactionKey: 'force_complete.click.submit',
      orderId,
      customerId,
      context: { reason },
    },
  })
}

export const trackOrderDetailAdminLinksOpened = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User opened the Admin links dropdown on the order details page',
    data: {
      interactionKey: 'header.click.admin_links',
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailAdminLinkNavigated = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    element,
    linkOrderId,
  }: { orderId: string; customerId: string; element: AdminLinkElement; linkOrderId?: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: `User navigated to ${element} from the Admin links dropdown on the order details page`,
    data: {
      interactionKey: `header.navigate.${element}`,
      destinationPage: element,
      orderId,
      customerId,
      ...(linkOrderId ? { context: { orderId: linkOrderId } } : {}),
    },
  })
}

// ---------- Order Detail page: Modify order modal ----------

export const trackManageOrderModifyOrderClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Modify order modal on the order details page',
    data: {
      interactionKey: 'manage_order.close.modify_order',
      orderId,
      customerId,
    },
  })
}

export const trackModifyOrderRequesterSelected = (
  trackEvent: TrackEvent,
  { orderId, customerId, requester }: { orderId: string; customerId: string; requester: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a requester in the Modify order modal',
    data: {
      interactionKey: 'modify_order.select.requester',
      orderId,
      customerId,
      context: { requester },
    },
  })
}

export const trackModifyOrderReasonSelected = (
  trackEvent: TrackEvent,
  { orderId, customerId, reason }: { orderId: string; customerId: string; reason: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a reason in the Modify order modal',
    data: {
      interactionKey: 'modify_order.select.reason',
      orderId,
      customerId,
      context: { reason },
    },
  })
}

export const trackModifyOrderModificationSelected = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    modification,
  }: { orderId: string; customerId: string; modification: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a modification type in the Modify order modal',
    data: {
      interactionKey: 'modify_order.select.modification',
      orderId,
      customerId,
      context: { modification },
    },
  })
}

export const trackModifyOrderNextClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Next in the Modify order modal',
    data: {
      interactionKey: 'modify_order.click.next',
      orderId,
      customerId,
    },
  })
}

export const trackModifyOrderDeliveryWindowSelected = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    deliveryWindow,
  }: { orderId: string; customerId: string; deliveryWindow: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a delivery window in the Modify order modal',
    data: {
      interactionKey: 'modify_order.select.delivery_window',
      orderId,
      customerId,
      context: { deliveryWindow },
    },
  })
}

export const trackModifyOrderAdditionalNotesEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, notes }: { orderId: string; customerId: string; notes: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered additional notes in the Modify order modal',
    data: {
      interactionKey: 'modify_order.enter.additional_notes',
      orderId,
      customerId,
      context: { notes },
    },
  })
}

export const trackModifyOrderNotifyCustomerChecked = (
  trackEvent: TrackEvent,
  { orderId, customerId, checked }: { orderId: string; customerId: string; checked: boolean }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User toggled the notify customer checkbox in the Modify order modal',
    data: {
      interactionKey: 'modify_order.check.notify_customer',
      orderId,
      customerId,
      context: { checked: checked ? 'Y' : 'N' },
    },
  })
}

export const trackModifyOrderResetOrderPickingChecked = (
  trackEvent: TrackEvent,
  { orderId, customerId, checked }: { orderId: string; customerId: string; checked: boolean }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User toggled the reset order picking checkbox in the Modify order modal',
    data: {
      interactionKey: 'modify_order.check.reset_order_picking',
      orderId,
      customerId,
      context: { checked: checked ? 'Y' : 'N' },
    },
  })
}

export const trackModifyOrderSubmitted = (
  trackEvent: TrackEvent,
  { orderId, customerId, submitType }: { orderId: string; customerId: string; submitType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User submitted the Modify order modal',
    data: {
      interactionKey: 'modify_order.click.submit',
      orderId,
      customerId,
      context: { submitType },
    },
  })
}

// ---------- Order Detail page: Order summary tabs ----------

export const trackOrderSummaryOrderDetailsTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Order details tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.order_details',
      orderId,
      customerId,
    },
  })
}

export const trackOrderSummaryChargeSummaryTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Charge summary tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.charge_summary',
      orderId,
      customerId,
    },
  })
}

export const trackOrderSummaryChargeLogsTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Charge logs tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.charge_logs',
      orderId,
      customerId,
    },
  })
}

export const trackOrderSummaryOrderChargesTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Order charges tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.order_charges',
      orderId,
      customerId,
    },
  })
}

export const trackOrderSummaryCouponsTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Coupons tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.coupons',
      orderId,
      customerId,
    },
  })
}

export const trackOrderSummaryResolutionAndAppeasementTabClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected the Resolution and appeasement tab in the order summary section',
    data: {
      interactionKey: 'order_summary.tab.resolution_and_appeasement',
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Order details tab ----------

export const trackOrderDetailsFullDetailsClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User opened the full order details panel on the order details page',
    data: {
      interactionKey: 'order_details.click.full_details',
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsFullDetailsClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the full order details panel on the order details page',
    data: {
      interactionKey: 'order_details.close.full_details',
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsSourceSiteNavigated = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    sourceSiteUrl,
  }: { orderId: string; customerId: string; sourceSiteUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to the source site from the order details tab',
    data: {
      interactionKey: 'order_details.navigate.source_site',
      destinationPage: sourceSiteUrl,
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsRatingLinkNavigated = (
  trackEvent: TrackEvent,
  { orderId, customerId, ratingUrl }: { orderId: string; customerId: string; ratingUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to the rating link from the order details tab',
    data: {
      interactionKey: 'order_details.navigate.rating_link',
      destinationPage: ratingUrl,
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsCustomerReceiptNavigated = (
  trackEvent: TrackEvent,
  { orderId, customerId, receiptUrl }: { orderId: string; customerId: string; receiptUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to the customer receipt from the order details tab',
    data: {
      interactionKey: 'order_details.navigate.customer_receipt',
      destinationPage: receiptUrl,
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsViewOnMapNavigated = (
  trackEvent: TrackEvent,
  { orderId, customerId, mapUrl }: { orderId: string; customerId: string; mapUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to the map view from the order details tab',
    data: {
      interactionKey: 'order_details.navigate.view_on_map',
      destinationPage: mapUrl,
      orderId,
      customerId,
    },
  })
}

export const trackOrderDetailsCustomerProfileNavigated = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to the customer profile from the order details tab',
    data: {
      interactionKey: 'order_details.navigate.customer_profile',
      destinationPage: 'customer_details' satisfies DestinationPage,
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Charge logs tab ----------

export const trackChargeLogsChargeLogIdNavigated = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    chargeLogId,
    chargeLogUrl,
  }: { orderId: string; customerId: string; chargeLogId: string; chargeLogUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to a charge log from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.navigate.charge_log_id',
      destinationPage: chargeLogUrl,
      orderId,
      customerId,
      context: { chargeLogId },
    },
  })
}

export const trackChargeLogsPaymentMethodNavigated = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    paymentMethodUrl,
  }: { orderId: string; customerId: string; paymentMethodUrl: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User navigated to a payment method from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.navigate.payment_method',
      destinationPage: paymentMethodUrl,
      orderId,
      customerId,
    },
  })
}

export const trackChargeLogsDownloadReceiptClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Download receipt in the charge logs tab',
    data: {
      interactionKey: 'charge_logs.click.download_receipt',
      orderId,
      customerId,
    },
  })
}

export const trackChargeLogsChangePaymentMethodClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User opened the Change payment method modal from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.click.change_payment_method',
      orderId,
      customerId,
    },
  })
}

export const trackChargeLogsChangePaymentMethodClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Change payment method modal from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.close.change_payment_method',
      orderId,
      customerId,
    },
  })
}

export const trackChargeLogsAddChargeClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User opened the Add charge modal from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.click.add_charge',
      orderId,
      customerId,
    },
  })
}

export const trackChargeLogsAddChargeClosed = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User closed the Add charge modal from the charge logs tab',
    data: {
      interactionKey: 'charge_logs.close.add_charge',
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Change payment method modal ----------

export const trackChangePaymentMethodCardSelected = (
  trackEvent: TrackEvent,
  {
    orderId,
    customerId,
    cardDescription,
  }: { orderId: string; customerId: string; cardDescription: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a card in the Change payment method modal',
    data: {
      interactionKey: 'change_payment_method.select.select_card',
      orderId,
      customerId,
      context: { cardDescription },
    },
  })
}

export const trackChangePaymentMethodSubmitted = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User submitted the Change payment method modal',
    data: {
      interactionKey: 'change_payment_method.click.submit',
      orderId,
      customerId,
    },
  })
}

// ---------- Order Detail page: Add charge modal ----------

export const trackAddChargeReasonSelected = (
  trackEvent: TrackEvent,
  { orderId, customerId, reason }: { orderId: string; customerId: string; reason: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a reason in the Add charge modal',
    data: {
      interactionKey: 'add_charge.select.reason',
      orderId,
      customerId,
      context: { reason },
    },
  })
}

export const trackAddChargeAmountEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, amount }: { orderId: string; customerId: string; amount: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered a charge amount in the Add charge modal',
    data: {
      interactionKey: 'add_charge.enter.charge_amount',
      orderId,
      customerId,
      context: { amount },
    },
  })
}

export const trackAddChargeContinueClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Continue in the Add charge modal',
    data: {
      interactionKey: 'add_charge.click.continue',
      orderId,
      customerId,
    },
  })
}

export const trackAddChargeFeeTypeSelected = (
  trackEvent: TrackEvent,
  { orderId, customerId, feeType }: { orderId: string; customerId: string; feeType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User selected a fee type in the Add charge modal',
    data: {
      interactionKey: 'add_charge.select.fee_type',
      orderId,
      customerId,
      context: { feeType },
    },
  })
}

export const trackAddChargeOverrideClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked the override option in the Add charge modal',
    data: {
      interactionKey: 'add_charge.click.override',
      orderId,
      customerId,
    },
  })
}

export const trackAddChargeOverrideEntered = (
  trackEvent: TrackEvent,
  { orderId, customerId, override }: { orderId: string; customerId: string; override: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User entered an override value in the Add charge modal',
    data: {
      interactionKey: 'add_charge.enter.override',
      orderId,
      customerId,
      context: { override },
    },
  })
}

// ---------- Order Detail page: Order charges tab ----------

export const trackOrderChargesWaiveClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId, feeType }: { orderId: string; customerId: string; feeType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Waive in the order charges tab',
    data: {
      interactionKey: 'order_charges.click.waive',
      orderId,
      customerId,
      context: { feeType },
    },
  })
}

export const trackOrderChargesRefundClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId, feeType }: { orderId: string; customerId: string; feeType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Refund in the order charges tab',
    data: {
      interactionKey: 'order_charges.click.refund',
      orderId,
      customerId,
      context: { feeType },
    },
  })
}

export const trackOrderChargesCreditClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId, feeType }: { orderId: string; customerId: string; feeType: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Credit in the order charges tab',
    data: {
      interactionKey: 'order_charges.click.credit',
      orderId,
      customerId,
      context: { feeType },
    },
  })
}

// ---------- Order Detail page: Resolution and appeasement tab ----------

export const trackResolutionAndAppeasementSendConfirmationEmailClicked = (
  trackEvent: TrackEvent,
  { orderId, customerId }: { orderId: string; customerId: string }
) => {
  trackEvent({
    id: ORDER_DETAIL_EVENT_ID,
    description: 'User clicked Send confirmation email in the resolution and appeasement tab',
    data: {
      interactionKey: 'resolution_and_appeasement.click.send_confirmation_email',
      orderId,
      customerId,
    },
  })
}
